import * as React from 'react'

import { Info } from 'lucide-react'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { cn } from '~/client/src/shared/utils/cn'
import { useOverlayPortalContainer } from '~/client/src/shared/utils/hostPortalContainer'

// 'blocked' stops the booking; 'warning' lets it through with a note.
export type WarningNoteTone = 'warning' | 'blocked'

export interface INoteLine {
  status: string
  text: string
  // Behind the info icon.
  details: string[]
}

export interface IWarningNote {
  tone: WarningNoteTone
  // The resource the note is about, where the surrounding UI doesn't name it.
  title?: string
  lines: INoteLine[]
}

interface IProps {
  note: IWarningNote
  isTitleShown?: boolean
  isEveryLineShown?: boolean
  className?: string
}

const TONES = {
  blocked: 'tw:bg-status-rejected-soft tw:text-status-rejected-soft-foreground',
  warning: 'tw:bg-status-amended-soft tw:text-status-amended-soft-foreground',
}

const stopPropagation = (event: React.SyntheticEvent) => event.stopPropagation()

// Opens on hover and on tap, so touch screens reach it too. Clicks stay here:
// the note sits inside selectable rows.
const NoteDetails: React.FC<{ details: string[] }> = ({ details }) => {
  const portalContainer = useOverlayPortalContainer()
  return (
    <Popover>
      <PopoverTrigger
        openOnHover
        delay={0}
        aria-label={Localization.translator.details}
        onClick={stopPropagation}
        className="tw:ml-1 tw:inline-flex tw:size-3.5 tw:cursor-pointer tw:items-center tw:justify-center tw:border-0 tw:bg-transparent tw:p-0 tw:align-[-2px] tw:text-muted-foreground"
      >
        <Info className="tw:size-3.5" />
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        portalContainer={portalContainer}
        onClick={stopPropagation}
        className="tw:w-fit tw:max-w-xs tw:gap-0 tw:rounded-md tw:bg-foreground tw:px-3 tw:py-1.5 tw:text-xs tw:text-background tw:shadow-none tw:ring-0"
      >
        {details.map(detail => (
          <span key={detail}>{detail}</span>
        ))}
      </PopoverContent>
    </Popover>
  )
}

// A time never breaks before its AM/PM.
const keepTimesWhole = (text: string) => text.replace(/ (AM|PM)\b/g, '\u00a0$1')

const NoteLine: React.FC<{
  line: INoteLine
  tone: WarningNoteTone
  children?: React.ReactNode
}> = ({ line, tone, children }) => (
  <div className="tw:flex tw:items-start tw:gap-1 tw:text-app-micro tw:font-medium">
    <span className={cn('tw:shrink-0 tw:rounded-sm tw:px-1', TONES[tone])}>
      {line.status}
    </span>
    <span className="tw:shrink-0 tw:text-tertiary-foreground">·</span>
    <span className="tw:min-w-0 tw:text-foreground">
      {keepTimesWhole(line.text)}
      {line.details.length > 0 && <NoteDetails details={line.details} />}
      {children}
    </span>
  </div>
)

interface ITonedLine {
  line: INoteLine
  tone: WarningNoteTone
}

const MoreLines: React.FC<{ items: ITonedLine[] }> = ({ items }) => {
  const portalContainer = useOverlayPortalContainer()
  return (
    <Popover>
      <PopoverTrigger
        openOnHover
        delay={0}
        aria-label={Localization.translator.xMore(items.length)}
        onClick={stopPropagation}
        className="tw:ml-1.5 tw:inline-flex tw:cursor-pointer tw:rounded-sm tw:border-0 tw:bg-muted tw:px-1 tw:text-app-micro tw:font-medium tw:text-muted-foreground"
      >
        +{items.length}
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        portalContainer={portalContainer}
        onClick={stopPropagation}
        className="tw:w-fit tw:max-w-xs tw:gap-1 tw:px-2.5 tw:py-2"
      >
        {items.map(({ line, tone }) => (
          <NoteLine
            key={`${line.status} ${line.text}`}
            line={line}
            tone={tone}
          />
        ))}
      </PopoverContent>
    </Popover>
  )
}

const toTonedLines = (note: IWarningNote): ITonedLine[] =>
  note.lines.map(line => ({ line, tone: note.tone }))

// Several notes about one thing read as its most serious problem, the rest
// behind a count. Blocking lines come first.
export const WarningNoteSummary: React.FC<{ notes: IWarningNote[] }> = ({
  notes,
}) => {
  const [first, ...others] = [
    ...notes.filter(note => note.tone === 'blocked'),
    ...notes.filter(note => note.tone !== 'blocked'),
  ].flatMap(toTonedLines)
  if (!first) return null

  return (
    <NoteLine line={first.line} tone={first.tone}>
      {others.length > 0 && <MoreLines items={others} />}
    </NoteLine>
  )
}

const WarningNote: React.FC<IProps> = ({
  note,
  isTitleShown,
  isEveryLineShown,
  className,
}) => (
  <div className={cn('tw:flex tw:min-w-0 tw:flex-col tw:gap-1', className)}>
    {isTitleShown && note.title && (
      <span className="tw:text-app-micro tw:font-medium tw:text-foreground">
        {note.title}
      </span>
    )}
    {isEveryLineShown ? (
      note.lines.map(line => (
        <NoteLine
          key={`${line.status} ${line.text}`}
          line={line}
          tone={note.tone}
        />
      ))
    ) : (
      <WarningNoteSummary notes={[note]} />
    )}
  </div>
)

export default WarningNote
